// src/components/EnterpriseNewDealModal.js
import React, { useState, useEffect } from "react";
import { X, Plus } from "lucide-react";
import { useApi } from "../hooks/useApi";
import CreateDealModal from "./CreateDealModal";

const EnterpriseNewDealModal = ({ isOpen, onClose, refreshDeals, onDealSelected, existingDealIds = [] }) => {
    const [deals, setDeals] = useState([]);
    const [selectedDealId, setSelectedDealId] = useState("");
    const [showNewDealModal, setShowNewDealModal] = useState(false);
    const [loadingDeals, setLoadingDeals] = useState(true);

    const apiCall = useApi();

    const org = JSON.parse(localStorage.getItem("selectedOrganization"));
      //console.log("Raw tenant:", tenant);
    const organizationId = org?.organizationId;

    // Fetch all deals when modal opens
    useEffect(() => {
        if (isOpen) fetchDeals();
    }, [isOpen]);

    // Auto-open create modal if no deals available
    useEffect(() => {
        if (isOpen && !loadingDeals && deals.length === 0) {
            setShowNewDealModal(true);
        }
    }, [isOpen, loadingDeals, deals.length]);

    const fetchDeals = async () => {
        try {
            const res = await apiCall("GET", `/deals`);
            // Filter out deals that are already added for this product category
            const availableDeals = (res || []).filter(d => !existingDealIds.includes(d.dealID));
            setDeals(availableDeals);
        } catch (err) {} finally {
            setLoadingDeals(false);
        }
    };

    // Handle new deal creation from nested modal
    const handleNewDealCreated = async (newDeal) => {
        setShowNewDealModal(false);
        // Close both modals and trigger refresh via parent callback
        onDealSelected?.(newDeal);
        onClose();
    };

    // Handle save (deal selection confirmation)
    // Handle save (deal selection confirmation)
    const handleSave = async (e) => {
        e.preventDefault();

        if (!selectedDealId) {
            alert("Please select a deal first.");
            return;
        }

        const selectedDeal = deals.find((d) => d.dealID.toString() === selectedDealId);

        try {
            // ✅ Step 1: Update DealCategory in backend
            await apiCall("PUT",`/deals/dealUpdate-category/${selectedDealId}`,{dealCategory: "Enterprise"});
            //console.log(`✅ Deal category updated for dealID ${selectedDealId}`);

            // ✅ Step 2: Trigger parent callback and close modal
            onDealSelected?.(selectedDeal);
            onClose();
        } catch (error) {
            alert("Failed to update deal category. Check console for details.");
        }
    };


    if (!isOpen) return null;

    return (
        <>
            {/* Assign Deal Modal */}
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-6 border border-border w-full max-w-lg shadow-lg rounded-md bg-theme-secondary">

                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-content-primary">Select or Create Deal</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    <form onSubmit={handleSave} className="space-y-4">
                        {/* Deal Dropdown + Add Button */}


                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Deal</label>
                            <div className="flex space-x-2 w-full">
                                <select
                                    value={selectedDealId}
                                    onChange={(e) => setSelectedDealId(e.target.value)}
                                    className="flex-grow min-w-0 border border-gray-300 rounded-md px-3 py-2 truncate"
                                    disabled={loadingDeals}
                                    style={{ maxWidth: "calc(100% - 40px)" }} // leave room for + button
                                >
                                    <option value="">-- Select a Deal --</option>
                                    {deals.map((d) => (
                                        <option
                                            key={d.dealID}
                                            value={d.dealID}
                                            className="truncate"
                                            title={d.dealName}
                                        >
                                            {d.dealName}
                                        </option>
                                    ))}
                                </select>

                                <button
                                    type="button"
                                    onClick={() => setShowNewDealModal(true)}
                                    className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex-shrink-0 flex items-center justify-center"
                                    title="Add New Deal"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>
                        </div>




                        {/* Buttons */}
                        <div className="flex justify-end space-x-3 mt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                                Save
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Nested New Deal Modal */}
            <CreateDealModal
                isOpen={showNewDealModal}
                onClose={deals.length === 0 ? onClose : () => setShowNewDealModal(false)}
                onDealCreated={handleNewDealCreated}
                dealCategory="Enterprise"
                organizationId={organizationId}
                title="Create New Enterprise Deal"
            />
        </>
    );
};

export default EnterpriseNewDealModal;
